import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getFileAuditData } from "../lib/api";
import { techDisplayName } from "../lib/names";

/* ═══════════════════════════════════════════════════════════════════════════
   AUDIT TYPE DEFINITIONS
   ═══════════════════════════════════════════════════════════════════════════ */
const AUDIT_ICONS = {
  combined:      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/></svg>,
  internal:      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>,
  accreditation: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
  hospital:      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0H5m0 0H3m12-13h-4m2-2v4M7 21v-4a2 2 0 012-2h2a2 2 0 012 2v4"/></svg>,
  medicare:      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/></svg>,
};
const AUDIT_COLORS = {
  combined:      { color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
  internal:      { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  accreditation: { color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4" },
  hospital:      { color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
  medicare:      { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
};
/* Who the report is about. This replaced five report-type views whose buttons
   were not being used. Note AUDIENCES is about the audit a CHECK belongs to
   (its audit_type), which is the same split the case page uses. */
const AUDIENCES = [
  { key: "both", label: "Both",                  desc: "Technologist and IP" },
  { key: "tech", label: "Technologist",          desc: "Tech checks only" },
  { key: "ip",   label: "Interpreting Physician", desc: "IP checks only" },
];
const AUDIENCE_COLORS = {
  both: { color: "#3b82f6", bg: "#eff6ff" },
  tech: { color: "#0891b2", bg: "#ecfeff" },
  ip:   { color: "#7c3aed", bg: "#f5f3ff" },
};

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED UI COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */
const CheckIcon = () => <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round"><polyline points="2 6 5 9 10 3"/></svg>;
const XIcon = () => <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="3" x2="9" y2="9"/><line x1="9" y1="3" x2="3" y2="9"/></svg>;
const QIcon = () => <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round"><circle cx="6" cy="6" r="5"/><path d="M5 4.5a1 1 0 012 0c0 .8-.8 1.2-.8 2"/><circle cx="6" cy="9" r=".5" fill="#d97706" stroke="none"/></svg>;

// Pooled, points-weighted score across a set of documents. Averaging each
// document's percentage would let a three-check case count as much as a
// seventy-check one. Technologist track; IP is a separate audit.
function pooledScore(docs) {
  let earned = 0, possible = 0;
  (docs || []).forEach(d => {
    earned   += Number(d.tech_earned_points)   || 0;
    possible += Number(d.tech_possible_points) || 0;
  });
  return possible > 0 ? Math.round((earned / possible) * 100) : null;
}

function ScoreRing({ score, size = 48 }) {
  const stroke = 4, radius = (size - stroke) / 2, circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score === 100 ? "#16a34a" : score >= 75 ? "#d97706" : "#dc2626";
  const bg = score === 100 ? "#dcfce7" : score >= 75 ? "#fef9c3" : "#fee2e2";
  return (
    <div style={{ position:"relative", width:size, height:size, display:"inline-flex", alignItems:"center", justifyContent:"center" }}>
      <svg width={size} height={size} style={{ transform:"rotate(-90deg)", position:"absolute" }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={bg} strokeWidth={stroke}/>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"/>
      </svg>
      <span style={{ fontSize: size > 40 ? 13 : 10, fontWeight:700, color, fontFamily:"var(--font-mono)", position:"relative" }}>{score}%</span>
    </div>
  );
}

function SectionHeader({ title, subtitle, borderColor = "var(--blue-600, #3b82f6)" }) {
  return (
    <div className="print-keep-next" style={{ padding:"12px 20px", borderBottom:"1px solid var(--border)", borderLeft:`3px solid ${borderColor}`, background:"var(--gray-50)" }}>
      <div style={{ fontWeight:600, fontSize:13, color:"var(--text-primary)" }}>{title}</div>
      {subtitle && <div style={{ fontSize:11, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)", marginTop:1 }}>{subtitle}</div>}
    </div>
  );
}

/* The audit level: one heading per audit with its category sections beneath,
   mirroring how the case page reads. */
function AuditGroupHeader({ title, subtitle, color }) {
  return (
    <div className="print-keep-next" style={{ padding:"14px 20px", borderTop:"1px solid var(--border)",
                  borderBottom:`2px solid ${color}`, background:"var(--white)" }}>
      <div style={{ fontFamily:"var(--font-mono)", fontSize:11, fontWeight:700,
                    letterSpacing:"0.08em", textTransform:"uppercase", color }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize:11, color:"var(--text-tertiary)",
                      fontFamily:"var(--font-mono)", marginTop:3 }}>{subtitle}</div>
      )}
    </div>
  );
}

function MetaGrid({ items }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px, 1fr))", gap:"8px 20px", padding:"14px 20px", borderBottom:"1px solid var(--border)" }}>
      {items.map((item, i) => (
        <div key={i}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:2 }}>{item.label}</div>
          <div style={{ fontSize:12, color:"var(--text-primary)", fontWeight:500 }}>{item.value || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{ padding:"14px 18px", background:"var(--white)", border:"1px solid var(--border)", borderRadius:8 }}>
      <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:700, color: color || "var(--text-primary)", fontFamily:"var(--font-mono)" }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:"var(--text-tertiary)", marginTop:2 }}>{sub}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   QA CHECK ROW — expandable with AI evidence
   ═══════════════════════════════════════════════════════════════════════════ */
function QACheckRow({ check, showEvidence = true, expandAll = false }) {
  // Seeded from the page-wide setting and re-synced whenever it changes, so
  // Expand all opens every row while a single row can still be toggled after.
  // Evidence that is open is in the DOM, so print picks it up with no extra
  // print rule - "expanded" and "in the PDF" are the same state.
  // A check flagged SensitiveEvidence in qa_checks.csv answers with a patient
  // identifier, so Expand all leaves it closed. It can still be opened by
  // hand - the reviewer needs to see it to do the QA - it just never opens
  // in bulk, and so never lands in a PDF by accident.
  const sensitive = !!check.sensitive;
  const [open, setOpen] = useState(expandAll && !sensitive);
  useEffect(() => { setOpen(expandAll && !sensitive); }, [expandAll, sensitive]);
  const icon = check.result === "PASS" ? <CheckIcon/> : check.result === "FAIL" ? <XIcon/> : <QIcon/>;
  const color = check.result === "PASS" ? "#16a34a" : check.result === "FAIL" ? "#dc2626" : "#d97706";
  const bg = check.result === "PASS" ? "#f0fdf4" : check.result === "FAIL" ? "#fef2f2" : "#fffbeb";

  return (
    <div style={{ borderBottom:"1px solid var(--border)" }}>
      <div className="print-row" onClick={() => showEvidence && setOpen(!open)} style={{
        padding:"8px 16px", display:"flex", alignItems:"center", gap:8,
        cursor: showEvidence ? "pointer" : "default", background: open ? bg : "transparent", transition:"background 0.1s"
      }}>
        <div style={{ width:20, height:20, borderRadius:4, background:bg, border:`1px solid ${color}22`,
          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{icon}</div>
        <span style={{ flex:1, fontSize:12, color:"var(--text-primary)", minWidth:0 }}>{check.display_question || check.question}</span>
        <span style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", flexShrink:0 }}>{check.check_id}</span>
        <span style={{ fontFamily:"var(--font-mono)", fontSize:10, fontWeight:600, color, padding:"1px 6px", borderRadius:3, background:bg, flexShrink:0 }}>{check.result}</span>
        {check.reviewer_decision && (
          <span style={{ fontFamily:"var(--font-mono)", fontSize:9, padding:"1px 5px", borderRadius:3, flexShrink:0,
            background: check.reviewer_decision.includes("PASS") ? "#dcfce7" : "#fee2e2",
            color: check.reviewer_decision.includes("PASS") ? "#16a34a" : "#dc2626" }}>
            {check.reviewer_decision.includes("OVERRIDE") ? "OVERRIDDEN" : "CONFIRMED"}
          </span>
        )}
        {showEvidence && (
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" style={{ transform:open?"rotate(180deg)":"none", transition:"transform 0.15s", flexShrink:0 }}>
            <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      {open && showEvidence && (
        <div style={{ padding:"0 16px 12px 44px" }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>AI Evidence</div>
          <div style={{ fontSize:11.5, color:"var(--text-secondary)", lineHeight:1.6, whiteSpace:"pre-wrap",
            padding:"8px 12px", background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)" }}>
            {(() => {
              const raw = (check.evidence || "").replace(/\s*\|\s*$/, "").trim();
              if (!raw) return check.result === "PASS" ? "Check passed — criteria met." : "No evidence recorded.";
              return raw.replace(/(\d{2}:\d{2}:\d{2}\s*\|)/g, "\n$1").trim();
            })()}
          </div>
          {check.reviewer_comment && (
            <div style={{ marginTop:6, fontSize:11, fontStyle:"italic", color:"var(--text-tertiary)" }}>
              Reviewer: "{check.reviewer_comment}" — {check.reviewer_name}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ALERT SECTION
   ═══════════════════════════════════════════════════════════════════════════ */
function AlertSection({ alerts }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div style={{ margin:"12px 20px", padding:"12px 14px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8 }}>
      <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"#dc2626", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8, fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {alerts.length} Intraoperative Alert{alerts.length > 1 ? "s" : ""}
      </div>
      {alerts.map((a, i) => (
        <div key={i} style={{ padding:"8px 0", borderTop: i > 0 ? "1px solid #fecaca" : "none", fontSize:12 }}>
          <div style={{ display:"flex", gap:6, marginBottom:3, flexWrap:"wrap" }}>
            <span style={{ fontFamily:"var(--font-mono)", fontSize:10, background:"#fee2e2", color:"#dc2626", padding:"1px 6px", borderRadius:3, fontWeight:600 }}>{a.type || "ALERT"}</span>
            {a.time && <span style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"#991b1b" }}>{a.time}</span>}
            {a.modality && <span style={{ fontFamily:"var(--font-mono)", fontSize:10, background:"#f0f7ff", color:"#3b82f6", padding:"1px 6px", borderRadius:3 }}>{a.modality}</span>}
            {a.severity && <span style={{ fontFamily:"var(--font-mono)", fontSize:9, padding:"1px 5px", borderRadius:3, fontWeight:600,
              background: a.severity === "HIGH" ? "#fee2e2" : a.severity === "MEDIUM" ? "#fef9c3" : "#f0fdf4",
              color: a.severity === "HIGH" ? "#dc2626" : a.severity === "MEDIUM" ? "#d97706" : "#16a34a" }}>{a.severity}</span>}
          </div>
          <div style={{ color:"var(--text-secondary)", lineHeight:1.5 }}>{a.description}</div>
          {a.evidence && <div style={{ marginTop:3, padding:"5px 10px", background:"#fff5f5", borderRadius:4, fontFamily:"var(--font-mono)", fontSize:10.5, color:"#991b1b", whiteSpace:"pre-wrap" }}>{a.evidence}</div>}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HELPER: extract timing from checks
   ═══════════════════════════════════════════════════════════════════════════ */
function extractTiming(doc) {
  const checks = doc.qa_checks || [];
  const _extractTime = (ev) => {
    if (!ev) return null;
    const colonMatch = ev.match(/\d{1,2}:\d{2}(?::\d{2})?(?:\s*[APap][Mm])?/);
    if (colonMatch) return colonMatch[0];
    const milMatch = ev.match(/\b(\d{4})\b/);
    if (milMatch) {
      const h = milMatch[1].substring(0, 2), m = milMatch[1].substring(2, 4);
      if (parseInt(h) < 24 && parseInt(m) < 60) return `${h}:${m}`;
    }
    return null;
  };
  const startCheck = checks.find(c => String(c.check_id) === "17");
  const stopCheck = checks.find(c => String(c.check_id) === "35") || checks.find(c => String(c.check_id) === "38");
  const startTime = startCheck?.result === "PASS" ? (_extractTime(startCheck.evidence) || "Documented") : null;
  const stopTime = stopCheck?.result === "PASS" ? (_extractTime(stopCheck.evidence) || "Documented") : null;
  let duration = null, units = null;
  if (startTime && stopTime) {
    try {
      const parseTime = (t) => {
        const m = t.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([APap][Mm]))?/);
        if (!m) return null;
        let h = parseInt(m[1]), min = parseInt(m[2]);
        if (m[4] && m[4].toUpperCase() === "PM" && h < 12) h += 12;
        if (m[4] && m[4].toUpperCase() === "AM" && h === 12) h = 0;
        return h * 60 + min;
      };
      const s = parseTime(startTime), e = parseTime(stopTime);
      if (s != null && e != null && e > s) {
        duration = e - s;
        units = Math.ceil(duration / 15);
      }
    } catch {}
  }
  return { startTime, stopTime, duration, units };
}

/* ═══════════════════════════════════════════════════════════════════════════
   DOCUMENT HEADER — shared across views
   ═══════════════════════════════════════════════════════════════════════════ */
function DocHeader({ doc, color = "var(--blue-600)", children }) {
  return (
    <div style={{ padding:"16px 20px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", gap:14 }}>
      <ScoreRing score={doc.qa_score || 0}/>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:14, fontWeight:600, marginBottom:2 }}>{doc.file_name}</div>
        <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>
          {doc.procedure_type} · {doc.facility || "—"} · {doc.processed_at ? new Date(doc.processed_at).toLocaleDateString() : "—"}
        </div>
      </div>
      {children}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   Which audit a check belongs to, and what order checks go in.

   audit_type is the authority. The old test guessed from the category name,
   treating only "Interpretation Report" and "Alert Handling" as IP - which
   put every other IP check into the Technologist section (15 of 24 on a
   typical case). Category cannot decide it anyway: both audits have a
   "Documentation" category, so grouping by category alone also merged two
   different sections into one.

   Order comes from Criteria_PrAppSortValue in qa_checks.csv, carried on each
   check as sort_value. Before this the page rendered in whatever order the
   search index returned.
   ─────────────────────────────────────────────────────────────────────────── */
const IP_AUDIT_TYPE = "Interpreting_Physician";
// Only consulted when a check carries no audit_type at all. "Alert Handling"
// is deliberately NOT here: it occurs under Technologist on real cases, so
// using it as an IP signal would move Tech checks into the IP group - the same
// class of mistake this replaced. "Interpretation Report" is IP by definition.
const LEGACY_IP_CATEGORIES = ["interpretation report"];
const SORT_VALUE_LAST = 10000;

function isIPCheck(c) {
  const at = (c.audit_type || "").trim();
  if (at) return at === IP_AUDIT_TYPE;
  // A result indexed before audit_type was carried falls back to the old test
  // rather than silently landing in the Technologist section.
  return LEGACY_IP_CATEGORIES.includes((c.category || "").toLowerCase());
}

/* Waveform checks arrive on doc.waveform.checks rather than doc.qa_checks,
   already carrying audit_type, category and criteria, so merging them here
   gives them the grouping, ordering, audience filter, print output and CSV
   without a single special case downstream. */
function allChecks(doc) {
  const qa = (doc && doc.qa_checks) || [];
  const wf = (doc && doc.waveform && doc.waveform.checks) || [];
  return wf.length ? qa.concat(wf) : qa;
}

function checkOrder(c) {
  const v = Number(c && c.sort_value);
  return Number.isFinite(v) ? v : SORT_VALUE_LAST;
}

function bySortValue(a, b) {
  const d = checkOrder(a) - checkOrder(b);
  if (d !== 0) return d;
  // Stable tiebreak, so equal sort values do not shuffle between renders.
  return String(a.check_id || "").localeCompare(
    String(b.check_id || ""), undefined, { numeric: true });
}

const AUDIT_LABELS = { TECH: "Technologist", IP: "Interpreting Physician" };
const AUDIT_COLORS_BY_KEY = { TECH: "#0891b2", IP: "#7c3aed" };

/* Three levels, in the order the config defines:

     Technologist                       <- audit (audit_type)
       Documentation                    <- category (CategoryValue)
         Digital Handshake              <- criteria (CriteriaValue)
           checks...

   Category and criteria are kept strictly apart because they share names at
   different levels: "Alert Handling" is a CriteriaValue under
   Technologist/Documentation and a CategoryValue under the IP audit. Keying a
   group on the name alone would merge the two.

   Every level is ordered by its earliest check's sort_value, so one config
   column drives the whole page. */
function auditGroupsFor(checks, audience = "both") {
  const audits = [];

  const pick = (list, key, make) => {
    let found = list.find(x => x.key === key);
    if (!found) { found = make(); list.push(found); }
    return found;
  };

  for (const c of checks || []) {
    const auditKey = isIPCheck(c) ? "IP" : "TECH";
    if (audience === "tech" && auditKey !== "TECH") continue;
    if (audience === "ip" && auditKey !== "IP") continue;

    const audit = pick(audits, auditKey, () => ({
      key: auditKey,
      label: AUDIT_LABELS[auditKey] || auditKey,
      color: AUDIT_COLORS_BY_KEY[auditKey] || "#3b82f6",
      categories: [],
    }));

    const categoryName = c.category || "Uncategorised";
    const category = pick(audit.categories, categoryName, () => ({
      key: categoryName, name: categoryName, groups: [],
    }));

    const criteriaName = (c.criteria || "").trim() || categoryName;
    const group = pick(category.groups, criteriaName, () => ({
      key: criteriaName, name: criteriaName, checks: [],
    }));

    group.checks.push(c);
  }

  /* The modality level, below criteria. It only earns a place when EVERY
     check in the group names a modality: waveform checks all do ('General'
     counts), while ModalityValue is blank on most qa_checks rows, so a
     document category is not fragmented by the handful that set it. */
  for (const audit of audits) {
    for (const cat of audit.categories) {
      for (const g of cat.groups) {
        const named = g.checks.filter(c => (c.modality || "").trim());
        const distinct = new Set(g.checks.map(c => (c.modality || "").trim()));
        g.modalities = null;
        if (named.length === g.checks.length && distinct.size > 1) {
          const mods = [];
          for (const c of g.checks) {
            const name = (c.modality || "").trim();
            let m = mods.find(x => x.key === name);
            if (!m) { m = { key: name, name, checks: [] }; mods.push(m); }
            m.checks.push(c);
          }
          g.modalities = mods;
        }
      }
    }
  }

  const first = list => (list.length ? checkOrder(list[0]) : SORT_VALUE_LAST);
  for (const audit of audits) {
    for (const category of audit.categories) {
      for (const group of category.groups) {
        group.checks.sort(bySortValue);
        if (group.modalities) {
          for (const m of group.modalities) m.checks.sort(bySortValue);
          group.modalities.sort((a, b) => first(a.checks) - first(b.checks));
        }
      }
      category.groups.sort((a, b) => first(a.checks) - first(b.checks));
    }
    audit.categories.sort(
      (a, b) => first(a.groups[0].checks) - first(b.groups[0].checks));
  }
  audits.sort((a, b) => (a.key === "TECH" ? -1 : 1) - (b.key === "TECH" ? -1 : 1));
  return audits;
}

/* A modality label, the innermost grouping. Rendered inline and small: it
   sits below a criteria heading, so it must not compete with it. */
function ModalityHeader({ title, subtitle }) {
  return (
    <div className="print-keep-next" style={{ padding:"5px 20px 5px 48px", background:"var(--gray-50)",
                  borderBottom:"1px solid var(--border)" }}>
      <span style={{ fontFamily:"var(--font-mono)", fontSize:9.5,
                     letterSpacing:"0.05em", color:"var(--text-tertiary)" }}>
        {title}{subtitle ? " · " + subtitle : ""}
      </span>
    </div>
  );
}

/* A criteria sub-heading, below a category and above its checks. */
function CriteriaHeader({ title, subtitle }) {
  return (
    <div className="print-keep-next" style={{ padding:"7px 20px 7px 34px", background:"var(--white)",
                  borderBottom:"1px solid var(--border)" }}>
      <span style={{ fontFamily:"var(--font-mono)", fontSize:10,
                     textTransform:"uppercase", letterSpacing:"0.06em",
                     color:"var(--text-secondary)", fontWeight:600 }}>{title}</span>
      {subtitle && (
        <span style={{ fontFamily:"var(--font-mono)", fontSize:10,
                       color:"var(--text-tertiary)", marginLeft:8 }}>{subtitle}</span>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMBINED VIEW — Full metadata + all checks with evidence + alerts
   ═══════════════════════════════════════════════════════════════════════════ */
function CombinedView({ doc, audience = "both", expandAll = false }) {
  const groups = useMemo(
    () => auditGroupsFor(allChecks(doc), audience), [doc, audience]);
  return (
    <div className="card" style={{ marginBottom:16, overflow:"hidden" }}>
      <DocHeader doc={doc}/>
      <MetaGrid items={[
        {label:"Procedure",value:doc.procedure_type},{label:"Surgeon",value:doc.surgeon_name},
        {label:"Technologist",value:techDisplayName(doc.technologist_name)},{label:"Monitoring Neurologist",value:doc.monitoring_neurologist},
        {label:"Modalities",value:doc.modalities_used},{label:"Facility",value:doc.facility},
        {label:"Processed",value:doc.processed_at?new Date(doc.processed_at).toLocaleString():"—"},
        {label:"Review Status",value:(doc.reviewer_status||"").replace(/_/g," ")},{label:"Reviewed By",value:doc.reviewed_by||"—"},
      ]}/>
      {groups.map(audit=>{
        // Counts roll up from the checks, so every heading agrees with the sum
        // of what is rendered beneath it.
        const cats = audit.categories.map(cat=>{
          const crits = cat.groups
            .map(g=>({ g, shown: g.checks.filter(c=>c.result!=="NOT_APPLICABLE") }))
            .filter(x=>x.shown.length);
          const shown = crits.reduce((a,x)=>a.concat(x.shown), []);
          return { cat, crits, shown };
        }).filter(x=>x.shown.length);
        if (!cats.length) return null;
        const all = cats.reduce((a,x)=>a.concat(x.shown), []);
        const passed = all.filter(c=>c.result==="PASS").length;
        return (
          <div key={audit.key}>
            <AuditGroupHeader title={audit.label} color={audit.color}
              subtitle={`${passed}/${all.length} passed · ${cats.length} categor${cats.length>1?"ies":"y"}`}/>
            {cats.map(({cat, crits, shown})=>(
              <div key={cat.key}>
                <SectionHeader title={cat.name} borderColor={audit.color}
                  subtitle={
                    cat.name === "Waveform Analysis" && doc.waveform
                      ? `${shown.filter(c=>c.result==="PASS").length}/${shown.length} passed · `
                        + (doc.waveform.score == null ? "unscored" : `${doc.waveform.score}%`)
                        + (doc.waveform.signed
                            ? ` · signed by ${doc.waveform.reviewer || "unknown"}`
                            : " · NOT SIGNED")
                      : `${shown.filter(c=>c.result==="PASS").length}/${shown.length} passed`
                  }/>
                {crits.map(({g, shown:cShown})=>(
                  <div key={g.key}>
                    {/* A criteria heading only earns its place when the
                        category has more than one. */}
                    {crits.length>1 && (
                      <CriteriaHeader title={g.name}
                        subtitle={`${cShown.filter(c=>c.result==="PASS").length}/${cShown.length}`}/>
                    )}
                    {g.modalities
                      ? g.modalities.map(m=>{
                          const mShown = m.checks.filter(c=>c.result!=="NOT_APPLICABLE");
                          if (!mShown.length) return null;
                          return (
                            <div key={m.key}>
                              <ModalityHeader title={m.name}
                                subtitle={`${mShown.filter(c=>c.result==="PASS").length}/${mShown.length}`}/>
                              {mShown.map(c=><QACheckRow key={c.check_id} check={c} showEvidence={true} expandAll={expandAll}/>)}
                            </div>
                          );
                        })
                      : cShown.map(c=><QACheckRow key={c.check_id} check={c} showEvidence={true} expandAll={expandAll}/>)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   INTERNAL QA VIEW — "What went wrong and what did we do about it?"
   ═══════════════════════════════════════════════════════════════════════════ */
function InternalQAView({ doc }) {
  const checks = doc.qa_checks || [];
  const applicable = checks.filter(c => c.result !== "NOT_APPLICABLE");
  const techChecks = applicable.filter(c => !isIPCheck(c)).sort(bySortValue);
  const ipChecks = applicable.filter(c => isIPCheck(c)).sort(bySortValue);

  const techPass = techChecks.filter(c => c.result === "PASS").length;
  const techTotal = techChecks.length;
  const ipPass = ipChecks.filter(c => c.result === "PASS").length;
  const ipTotal = ipChecks.length;

  const failed = applicable.filter(c => c.result === "FAIL");
  const utd = applicable.filter(c => c.result !== "PASS" && c.result !== "FAIL");
  const overridden = failed.filter(c => c.reviewer_decision === "OVERRIDE_PASS" || c.reviewer_decision === "OVERRIDE_FAIL");
  const confirmed = failed.filter(c => c.reviewer_decision === "CONFIRMED_FAIL");
  const pending = failed.filter(c => !c.reviewer_decision);

  // Auto-generate action items from failures
  const actionItems = useMemo(() => {
    const items = [];
    const failCategories = {};
    failed.forEach(c => { failCategories[c.category] = (failCategories[c.category] || 0) + 1; });
    const topCategory = Object.entries(failCategories).sort((a,b) => b[1] - a[1])[0];
    if (topCategory) items.push(`${topCategory[1]} failure(s) in "${topCategory[0]}" — review documentation standards for this section`);
    if (pending.length > 0) items.push(`${pending.length} failed check(s) still awaiting reviewer decision`);
    if (overridden.length > 0) items.push(`${overridden.length} AI decision(s) overridden by reviewer — consider prompt tuning for these checks`);
    if (items.length === 0) items.push("No action items — all checks passed or reviewed");
    return items;
  }, [failed, pending, overridden]);

  return (
    <div className="card" style={{ marginBottom:16, overflow:"hidden" }}>
      <DocHeader doc={doc}>
        <span style={{ fontFamily:"var(--font-mono)", fontSize:10, padding:"3px 10px", borderRadius:4,
          background: ["CLOSED","REVIEWED"].includes(doc.reviewer_status) ? "#dcfce7" : "#fef9c3",
          color: ["CLOSED","REVIEWED"].includes(doc.reviewer_status) ? "#16a34a" : "#d97706", fontWeight:600 }}>
          {(doc.reviewer_status || "PENDING").replace(/_/g, " ")}
        </span>
      </DocHeader>

      {/* Two-column score summary */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", borderBottom:"1px solid var(--border)" }}>
        <div style={{ padding:"14px 20px", borderRight:"1px solid var(--border)" }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Technologist Audit</div>
          <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
            <span style={{ fontSize:24, fontWeight:700, color: techTotal > 0 && techPass === techTotal ? "#16a34a" : "#2563eb", fontFamily:"var(--font-mono)" }}>
              {techTotal > 0 ? Math.round((techPass / techTotal) * 100) : 0}%
            </span>
            <span style={{ fontSize:11, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)" }}>{techPass}/{techTotal} passed</span>
          </div>
        </div>
        <div style={{ padding:"14px 20px" }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>IP Audit</div>
          <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
            <span style={{ fontSize:24, fontWeight:700, color: ipTotal > 0 && ipPass === ipTotal ? "#16a34a" : "#2563eb", fontFamily:"var(--font-mono)" }}>
              {ipTotal > 0 ? Math.round((ipPass / ipTotal) * 100) : 0}%
            </span>
            <span style={{ fontSize:11, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)" }}>{ipPass}/{ipTotal} passed</span>
          </div>
        </div>
      </div>

      {/* Technologist + reviewer info */}
      <MetaGrid items={[
        {label:"Technologist",value:techDisplayName(doc.technologist_name)},{label:"Surgeon",value:doc.surgeon_name},
        {label:"Reviewed By",value:doc.reviewed_by||"Pending"},{label:"Review Status",value:(doc.reviewer_status||"").replace(/_/g," ")},
      ]}/>

      {/* Action Items */}
      <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)" }}>
        <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"#2563eb", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8, fontWeight:600 }}>Action Items</div>
        {actionItems.map((item, i) => (
          <div key={i} style={{ display:"flex", gap:10, padding:"6px 0", alignItems:"flex-start" }}>
            <span style={{ background:"#2563eb", color:"white", borderRadius:"50%", width:18, height:18, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, flexShrink:0, marginTop:1 }}>{i+1}</span>
            <span style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.5 }}>{item}</span>
          </div>
        ))}
      </div>

      {/* Failed checks with evidence + reviewer decisions */}
      {failed.length > 0 && (
        <>
          <SectionHeader title={`Failed Checks (${failed.length})`}
            subtitle={`${confirmed.length} confirmed · ${overridden.length} overridden · ${pending.length} pending`}
            borderColor="#dc2626"/>
          {failed.map(c => <QACheckRow key={c.check_id} check={c} showEvidence={true}/>)}
        </>
      )}

      {/* UTD checks */}
      {utd.length > 0 && (
        <>
          <SectionHeader title={`Unable to Determine (${utd.length})`} borderColor="#d97706"/>
          {utd.map(c => <QACheckRow key={c.check_id} check={c} showEvidence={true}/>)}
        </>
      )}

      {/* Alerts */}
      <AlertSection alerts={doc.alerts}/>

      {failed.length === 0 && utd.length === 0 && (
        <div style={{ padding:20, textAlign:"center", color:"#16a34a", fontFamily:"var(--font-mono)", fontSize:12 }}>
          ✓ All {applicable.length} applicable checks passed
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ACCREDITATION VIEW — "Prove monitoring met standards"
   ═══════════════════════════════════════════════════════════════════════════ */
function AccreditationView({ doc }) {
  const checks = doc.qa_checks || [];
  const applicable = checks.filter(c => c.result !== "NOT_APPLICABLE");

  // Parse modalities into pills
  const modalities = (doc.modalities_used || "").split(",").map(m => m.trim()).filter(Boolean);

  // Category compliance
  // Points-weighted, like every other score. pass/total are earned and
  // possible points, not check tallies. A category with nothing scorable
  // is left out rather than shown as a zero.
  const catPerf = useMemo(() => {
    const cats = {};
    for (const c of applicable) {
      if (c.result === "UNABLE TO DETERMINE") continue;
      const w = Number(c.max_points) > 0 ? Number(c.max_points) : 1;
      if (!cats[c.category]) cats[c.category] = { pass: 0, total: 0 };
      cats[c.category].total += w;
      if (c.result === "PASS") cats[c.category].pass += w;
    }
    return Object.entries(cats)
      .filter(([, v]) => v.total > 0)
      .map(([name, v]) => ({ name, ...v, rate: Math.round((v.pass / v.total) * 100) }));
  }, [applicable]);

  // Modality-specific checks
  const modChecks = useMemo(() => {
    const groups = {};
    for (const c of applicable) {
      const q = (c.question || "").toUpperCase();
      for (const mod of ["SSEP", "MEP", "EMG", "EEG", "BAER"]) {
        if (q.includes(mod)) {
          if (!groups[mod]) groups[mod] = [];
          groups[mod].push(c);
        }
      }
    }
    return Object.entries(groups);
  }, [applicable]);

  return (
    <div className="card" style={{ marginBottom:16, overflow:"hidden" }}>
      {/* Formal header */}
      <div style={{ padding:"20px", borderBottom:"1px solid var(--border)", borderLeft:"3px solid #0d9488", background:"#f0fdfa" }}>
        <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"#0d9488", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>IONM Quality Assurance — Case Documentation</div>
        <div style={{ fontSize:16, fontWeight:700, color:"var(--text-primary)" }}>{doc.file_name}</div>
        <div style={{ fontSize:11, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)", marginTop:4 }}>
          Processed: {doc.processed_at ? new Date(doc.processed_at).toLocaleDateString() : "—"} · QA Score: {doc.qa_score}%
        </div>
      </div>

      {/* Case summary */}
      <MetaGrid items={[
        {label:"Procedure",value:doc.procedure_type},{label:"Diagnosis",value:"—"},
        {label:"Date of Service",value:doc.processed_at?new Date(doc.processed_at).toLocaleDateString():"—"},
        {label:"Facility",value:doc.facility},
        {label:"Technologist",value:techDisplayName(doc.technologist_name)},{label:"Supervising Neurologist",value:doc.monitoring_neurologist},
        {label:"Reviewer Sign-off",value:doc.reviewed_by?`${doc.reviewed_by} on ${doc.reviewed_at?new Date(doc.reviewed_at).toLocaleDateString():"—"}`:"Pending"},
      ]}/>

      {/* Modalities used */}
      <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)" }}>
        <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Modalities Performed</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {modalities.length > 0 ? modalities.map(mod => (
            <span key={mod} style={{ padding:"4px 12px", borderRadius:14, fontSize:11, fontWeight:600, fontFamily:"var(--font-mono)",
              background:"#f0fdfa", color:"#0d9488", border:"1px solid #99f6e4" }}>
              ✓ {mod}
            </span>
          )) : <span style={{ fontSize:12, color:"var(--text-tertiary)" }}>Not documented</span>}
        </div>
      </div>

      {/* Category compliance bars */}
      <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)" }}>
        <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10 }}>Compliance by Category</div>
        {catPerf.map(cat => (
          <div key={cat.name} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 0" }}>
            <span style={{ fontSize:12, flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{cat.name}</span>
            <div style={{ width:120, height:6, background:"var(--gray-200)", borderRadius:3, flexShrink:0 }}>
              <div style={{ width:`${cat.rate}%`, height:"100%", borderRadius:3, background:cat.rate===100?"#16a34a":cat.rate>=75?"#0d9488":"#dc2626" }}/>
            </div>
            <span style={{ fontFamily:"var(--font-mono)", fontSize:11, fontWeight:600, width:36, textAlign:"right",
              color:cat.rate===100?"#16a34a":cat.rate>=75?"#0d9488":"#dc2626" }}>{cat.rate}%</span>
            <span style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", width:40 }}>{cat.pass}/{cat.total}</span>
          </div>
        ))}
      </div>

      {/* Modality coverage */}
      {modChecks.length > 0 && (
        <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)" }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Modality-Specific Compliance</div>
          {modChecks.map(([mod, checks]) => {
            const p = checks.filter(c => c.result === "PASS").length;
            return (
              <div key={mod} style={{ marginBottom:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ fontFamily:"var(--font-mono)", fontSize:11, fontWeight:600, color:"#0d9488" }}>{mod}</span>
                  <span style={{ fontSize:10, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)" }}>{p}/{checks.length} passed</span>
                </div>
                {checks.filter(c => c.result === "FAIL").map(c => (
                  <div key={c.check_id} style={{ paddingLeft:16, fontSize:11, color:"#dc2626", padding:"2px 16px" }}>✗ {c.display_question || c.question}</div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Technologist log entry (CNIM format) */}
      <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)" }}>
        <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>CNIM Case Log Entry</div>
        <div style={{ padding:"10px 14px", background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", fontFamily:"var(--font-mono)", fontSize:11, lineHeight:1.8 }}>
          <div>Date: {doc.processed_at ? new Date(doc.processed_at).toLocaleDateString() : "—"}</div>
          <div>Procedure: {doc.procedure_type || "—"}</div>
          <div>Modalities: {doc.modalities_used || "—"}</div>
          <div>Facility: {doc.facility || "—"}</div>
          <div>QA Outcome: {doc.fail_count === 0 ? "PASSED" : "FAILED"} ({doc.qa_score}%)</div>
          <div>Technologist: {techDisplayName(doc.technologist_name) || "—"}</div>
        </div>
      </div>

      {/* Attestation + signature */}
      <div style={{ padding:"16px 20px" }}>
        <div style={{ padding:"14px 18px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, fontSize:12, lineHeight:1.7, color:"#14532d", marginBottom:16 }}>
          <strong style={{ display:"block", marginBottom:6 }}>Quality Assurance Program Attestation</strong>
          This case was monitored using standard IONM protocols and reviewed against established QA criteria. All findings have been documented and reviewed. This record is provided in support of accreditation and credentialing processes.
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:24, marginTop:20 }}>
          {["Authorized Signatory", "Title / Credentials", "Date"].map(label => (
            <div key={label}>
              <div style={{ borderBottom:"1px solid #94a3b8", height:32, marginBottom:4 }}/>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HOSPITAL VIEW — "Was this case safe?"
   ═══════════════════════════════════════════════════════════════════════════ */
function HospitalView({ documents }) {
  const bySurgeon = useMemo(() => {
    const g = {};
    for (const d of documents) { const s = d.surgeon_name || "Unknown"; if (!g[s]) g[s] = []; g[s].push(d); }
    return Object.entries(g).sort((a, b) => b[1].length - a[1].length);
  }, [documents]);

  return (
    <>{bySurgeon.map(([surgeon, docs]) => {
      const alertDocs = docs.filter(d => d.alert_status === "ALERT" || d.alert_status === "MANUAL_ALERT");
      const avg = pooledScore(docs);
      return (
        <div key={surgeon} className="card" style={{ marginBottom:16, overflow:"hidden" }}>
          <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)", borderLeft:"3px solid #7c3aed", background:"#faf5ff" }}>
            <div style={{ fontWeight:600, fontSize:14 }}>{surgeon}</div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-tertiary)", marginTop:2 }}>
              {docs.length} case{docs.length !== 1 ? "s" : ""} · Avg: {avg}%
              {alertDocs.length > 0 && <span style={{ color:"#dc2626", fontWeight:600 }}> · {alertDocs.length} alert case{alertDocs.length !== 1 ? "s" : ""}</span>}
            </div>
          </div>

          {docs.map(doc => {
            const hasAlert = doc.alert_status === "ALERT" || doc.alert_status === "MANUAL_ALERT";
            const commFailed = (doc.qa_checks || []).filter(c => c.result === "FAIL" && ((c.category || "").toLowerCase().includes("communication") || (c.category || "").toLowerCase().includes("alert")));

            return (
              <div key={doc.file_name} style={{ borderBottom:"1px solid var(--border)" }}>
                <div style={{ padding:"12px 20px", display:"flex", alignItems:"center", gap:12 }}>
                  <ScoreRing score={doc.qa_score || 0} size={36}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:500 }}>{doc.file_name}</div>
                    <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>
                      {doc.procedure_type} · {doc.facility || "—"} · {doc.processed_at ? new Date(doc.processed_at).toLocaleDateString() : "—"}
                    </div>
                  </div>
                  {!hasAlert && (
                    <span style={{ padding:"3px 10px", borderRadius:14, background:"#f0fdf4", border:"1px solid #bbf7d0", fontSize:10, fontWeight:600, color:"#16a34a", fontFamily:"var(--font-mono)" }}>
                      ✓ No safety concerns
                    </span>
                  )}
                  {hasAlert && (
                    <span style={{ display:"flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:14, background:"#fef2f2", border:"1px solid #fecaca", fontSize:10, fontWeight:600, color:"#dc2626", fontFamily:"var(--font-mono)" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/></svg>
                      {doc.alert_count} Alert{doc.alert_count !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                {/* Alert timeline */}
                {hasAlert && <AlertSection alerts={doc.alerts}/>}

                {/* Communication / alert failures */}
                {commFailed.length > 0 && (
                  <div style={{ padding:"0 20px 12px" }}>
                    <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"#dc2626", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Failed Communication / Alert Checks</div>
                    {commFailed.map(c => <QACheckRow key={c.check_id} check={c} showEvidence={true}/>)}
                  </div>
                )}

                {/* Quality summary — simplified for hospital admin */}
                <div style={{ padding:"8px 20px 12px", display:"flex", gap:16 }}>
                  <span style={{ fontSize:11, color:"var(--text-tertiary)" }}>Documentation: <strong style={{ color: doc.qa_score >= 80 ? "#16a34a" : "#d97706" }}>{doc.qa_score}%</strong></span>
                  <span style={{ fontSize:11, color:"var(--text-tertiary)" }}>Gaps: <strong style={{ color: doc.fail_count > 0 ? "#dc2626" : "#16a34a" }}>{doc.fail_count}</strong></span>
                  <span style={{ fontSize:11, color:"var(--text-tertiary)" }}>Technologist: <strong>{techDisplayName(doc.technologist_name)}</strong></span>
                </div>
              </div>
            );
          })}
        </div>
      );
    })}</>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MEDICARE VIEW — "Is this billable?"
   ═══════════════════════════════════════════════════════════════════════════ */
function MedicareView({ doc }) {
  const timing = extractTiming(doc);
  const checks = doc.qa_checks || [];

  // Billing-relevant checks
  const billingChecks = [
    { label: "Patient Identification", pass: checks.some(c => String(c.check_id) === "1" && c.result === "PASS") },
    { label: "IONM Start Time", pass: !!timing.startTime },
    { label: "IONM Stop Time", pass: !!timing.stopTime },
    { label: "Baseline Documentation", pass: checks.some(c => (c.question || "").toLowerCase().includes("baseline") && c.result === "PASS") },
    { label: "Continuous Monitoring Evidence", pass: checks.some(c => (c.question || "").toLowerCase().includes("anesthesia comment") && c.result === "PASS") },
    { label: "Interpretation Report Present", pass: checks.some(c => (c.question || "").toLowerCase().includes("interpretation report") && c.result === "PASS") },
    { label: "Technologist Present Throughout", pass: checks.some(c => (c.question || "").toLowerCase().includes("final trace") && c.result === "PASS") },
  ];

  const docGaps = [];
  if (!timing.startTime) docGaps.push("IONM START time not documented");
  if (!timing.stopTime) docGaps.push("IONM STOP time not documented");
  if (!doc.modalities_used || doc.modalities_used === "Unknown") docGaps.push("Modalities not documented");
  if (doc.monitoring_neurologist === "Unknown") docGaps.push("Monitoring neurologist not documented");

  const denialRisk = docGaps.length >= 2 ? "HIGH" : docGaps.length === 1 ? "MEDIUM" : "LOW";

  return (
    <div className="card" style={{ marginBottom:16, overflow:"hidden" }}>
      {/* Header with CPT codes */}
      <div style={{ padding:"16px 20px", borderBottom:"1px solid var(--border)", borderLeft:"3px solid #b45309", display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"#b45309", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>CPT 95940 / G0453 Compliance</div>
          <div style={{ fontSize:14, fontWeight:600 }}>{doc.file_name}</div>
          <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", marginTop:2 }}>
            Date of Service: {doc.processed_at ? new Date(doc.processed_at).toLocaleDateString() : "—"}
          </div>
        </div>
        <div style={{ padding:"6px 14px", borderRadius:6, fontFamily:"var(--font-mono)", fontSize:11, fontWeight:600,
          background: denialRisk === "LOW" ? "#f0fdf4" : denialRisk === "MEDIUM" ? "#fffbeb" : "#fef2f2",
          color: denialRisk === "LOW" ? "#16a34a" : denialRisk === "MEDIUM" ? "#d97706" : "#dc2626",
          border: `1px solid ${denialRisk === "LOW" ? "#bbf7d0" : denialRisk === "MEDIUM" ? "#fde68a" : "#fecaca"}` }}>
          Denial Risk: {denialRisk}
        </div>
      </div>

      {/* Monitoring window — prominent display */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr auto", gap:0, borderBottom:"1px solid var(--border)" }}>
        <div style={{ padding:"18px 20px", textAlign:"center" }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>IONM Start</div>
          <div style={{ fontSize:28, fontWeight:300, color: timing.startTime ? "#16a34a" : "#dc2626", fontFamily:"var(--font-mono)" }}>
            {timing.startTime || "NOT FOUND"}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", color:"var(--text-tertiary)", fontSize:20 }}>→</div>
        <div style={{ padding:"18px 20px", textAlign:"center" }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>IONM Stop</div>
          <div style={{ fontSize:28, fontWeight:300, color: timing.stopTime ? "#16a34a" : "#dc2626", fontFamily:"var(--font-mono)" }}>
            {timing.stopTime || "NOT FOUND"}
          </div>
        </div>
        <div style={{ padding:"18px 20px", textAlign:"center", borderLeft:"1px solid var(--border)", background:"var(--gray-50)" }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>Duration / Units</div>
          <div style={{ fontSize:22, fontWeight:700, color:"#b45309", fontFamily:"var(--font-mono)" }}>
            {timing.duration != null ? `${timing.duration} min` : "—"}
          </div>
          {timing.units != null && (
            <div style={{ fontSize:11, color:"#b45309", fontFamily:"var(--font-mono)", marginTop:2 }}>{timing.units} × 15-min units</div>
          )}
        </div>
      </div>

      {/* Medical necessity */}
      <MetaGrid items={[
        {label:"Procedure",value:doc.procedure_type},{label:"Surgeon",value:doc.surgeon_name},
        {label:"Modalities",value:doc.modalities_used||"Not documented"},{label:"Monitoring Neurologist",value:doc.monitoring_neurologist},
        {label:"Technologist",value:techDisplayName(doc.technologist_name)},{label:"Facility",value:doc.facility},
      ]}/>

      {/* Documentation completeness checklist */}
      <div style={{ padding:"14px 20px", borderBottom:"1px solid var(--border)" }}>
        <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10 }}>Documentation Completeness</div>
        {billingChecks.map((item, i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", borderBottom: i < billingChecks.length - 1 ? "1px solid var(--border)" : "none" }}>
            <div style={{ width:18, height:18, borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
              background: item.pass ? "#f0fdf4" : "#fef2f2", border: `1px solid ${item.pass ? "#16a34a22" : "#dc262622"}` }}>
              {item.pass ? <CheckIcon/> : <XIcon/>}
            </div>
            <span style={{ fontSize:12, color:"var(--text-primary)", flex:1 }}>{item.label}</span>
            <span style={{ fontFamily:"var(--font-mono)", fontSize:10, fontWeight:600, color: item.pass ? "#16a34a" : "#dc2626" }}>
              {item.pass ? "✓" : "✗"}
            </span>
          </div>
        ))}
      </div>

      {/* Documentation gaps / denial risk */}
      {docGaps.length > 0 && (
        <div style={{ margin:"12px 20px", padding:"10px 14px", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8 }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"#d97706", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6, fontWeight:600 }}>Billing Risk Flags</div>
          {docGaps.map((g, i) => (
            <div key={i} style={{ fontSize:12, color:"#92400e", padding:"2px 0", display:"flex", alignItems:"center", gap:6 }}>
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="#d97706" strokeWidth="2"><path d="M8 3v6M8 11v1"/></svg>{g}
            </div>
          ))}
        </div>
      )}

      {/* Billing summary row */}
      <div style={{ padding:"12px 20px", background:"var(--gray-50)", borderTop:"1px solid var(--border)" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:"var(--font-mono)" }}>
          <thead><tr style={{ borderBottom:"1px solid var(--border)" }}>
            {["Date","CPT","Start","Stop","Duration","Units","Modalities","Gaps"].map(h => (
              <th key={h} style={{ padding:"4px 6px", textAlign:"left", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", fontWeight:500 }}>{h}</th>
            ))}
          </tr></thead>
          <tbody><tr>
            <td style={{ padding:"4px 6px" }}>{doc.processed_at ? new Date(doc.processed_at).toLocaleDateString() : "—"}</td>
            <td style={{ padding:"4px 6px" }}>95940</td>
            <td style={{ padding:"4px 6px", color: timing.startTime ? "#16a34a" : "#dc2626", fontWeight:600 }}>{timing.startTime || "—"}</td>
            <td style={{ padding:"4px 6px", color: timing.stopTime ? "#16a34a" : "#dc2626", fontWeight:600 }}>{timing.stopTime || "—"}</td>
            <td style={{ padding:"4px 6px" }}>{timing.duration != null ? `${timing.duration} min` : "—"}</td>
            <td style={{ padding:"4px 6px", fontWeight:600 }}>{timing.units != null ? timing.units : "—"}</td>
            <td style={{ padding:"4px 6px", fontSize:10 }}>{doc.modalities_used || "—"}</td>
            <td style={{ padding:"4px 6px", color: docGaps.length > 0 ? "#dc2626" : "#16a34a" }}>{docGaps.length || "None"}</td>
          </tr></tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CROSS-FILE AGGREGATES — only shown for multi-file audits
   ═══════════════════════════════════════════════════════════════════════════ */
function AggregateSection({ documents, audience = "both" }) {
  if (documents.length <= 1) return null;

  // Respect the audience filter, or the summary at the top of the page would
  // report every check while the sections below it show one audit's worth.
  const inAudience = c =>
    audience === "both" ? true : audience === "ip" ? isIPCheck(c) : !isIPCheck(c);
  const checksOf = doc => allChecks(doc).filter(inAudience);

  const failCounts = {};
  for (const doc of documents) { for (const c of checksOf(doc)) { if (c.result==="FAIL") { if(!failCounts[c.check_id])failCounts[c.check_id]={question:c.display_question||c.question,category:c.category,count:0}; failCounts[c.check_id].count++; } } }
  const topFailed = Object.entries(failCounts).sort((a,b)=>b[1].count-a[1].count).slice(0,8);
  // Points-weighted; pass/total are earned and possible points.
  const catPerf = {};
  for (const doc of documents) {
    for (const c of checksOf(doc)) {
      if (c.result === "NOT_APPLICABLE" || c.result === "UNABLE TO DETERMINE") continue;
      const w = Number(c.max_points) > 0 ? Number(c.max_points) : 1;
      if (!catPerf[c.category]) catPerf[c.category] = { pass: 0, total: 0 };
      catPerf[c.category].total += w;
      if (c.result === "PASS") catPerf[c.category].pass += w;
    }
  }
  if (topFailed.length===0 && Object.keys(catPerf).length===0) return null;

  return (
    <div className="card" style={{ marginBottom:16, overflow:"hidden" }}>
      <SectionHeader title={`Cross-Case Analysis (${documents.length} cases)`} subtitle="Common patterns across selected cases" borderColor="#6366f1"/>
      {Object.keys(catPerf).length > 0 && (
        <div style={{ padding:"14px 20px", borderBottom: topFailed.length > 0 ? "1px solid var(--border)" : "none" }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10 }}>Category Compliance</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(240px, 1fr))", gap:6 }}>
            {Object.entries(catPerf).map(([cat, { pass, total }]) => {
              const pct = Math.round((pass / total) * 100);
              return (
                <div key={cat} style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:11, flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{cat}</span>
                  <div style={{ width:60, height:4, background:"var(--gray-200)", borderRadius:2, flexShrink:0 }}>
                    <div style={{ width:`${pct}%`, height:"100%", borderRadius:2, background:pct===100?"#16a34a":pct>=75?"#d97706":"#dc2626" }}/>
                  </div>
                  <span style={{ fontFamily:"var(--font-mono)", fontSize:10, fontWeight:600, color:pct===100?"#16a34a":pct>=75?"#d97706":"#dc2626", width:32, textAlign:"right" }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {topFailed.length > 0 && (
        <div style={{ padding:"14px 20px" }}>
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10 }}>Most Commonly Failed Checks</div>
          {topFailed.map(([cid, { question, category, count }]) => (
            <div key={cid} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 0", borderBottom:"1px solid var(--border)" }}>
              <span style={{ fontFamily:"var(--font-mono)", fontSize:18, fontWeight:700, color:"#dc2626", width:28, textAlign:"center" }}>{count}</span>
              <div style={{ flex:1 }}><div style={{ fontSize:12 }}>{question}</div><div style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>{category} · {cid}</div></div>
              <span style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)" }}>{count}/{documents.length} cases</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   EXPORT FUNCTIONS
   ═══════════════════════════════════════════════════════════════════════════ */
/* The CSV mirrors the page: the same audience filter, the same three-level
   grouping as columns, and rows in the same configured order. It walks
   auditGroupsFor rather than doc.qa_checks so the two can never drift. */
function exportToCSV(documents, audience = "both") {
  const META = ["File Name","Procedure","Surgeon","Technologist","Neurologist",
    "Facility","Modalities","QA Score","Pass","Fail","UTD","Alert Status",
    "Alert Count","Reviewer Status","Reviewed By"];
  const CHECK = ["Audit","Category","Criteria","Modality","Check ID","Question",
    "Result","Evidence","Reviewer Decision","Reviewer Comment"];
  const rows = [[...META, ...CHECK]];

  for (const doc of documents) {
    const base = [doc.file_name,doc.procedure_type,doc.surgeon_name,
      techDisplayName(doc.technologist_name),doc.monitoring_neurologist,
      doc.facility,doc.modalities_used,doc.qa_score,doc.pass_count,doc.fail_count,
      doc.total_checks-doc.pass_count-doc.fail_count,doc.alert_status,
      doc.alert_count,doc.reviewer_status,doc.reviewed_by];

    let wrote = 0;
    for (const audit of auditGroupsFor(allChecks(doc), audience)) {
      for (const cat of audit.categories) {
        for (const g of cat.groups) {
          for (const c of g.checks) {
            rows.push([...base, audit.label, cat.name, g.name,
              (c.modality||"").trim(), c.check_id,
              c.display_question||c.question, c.result,
              // A check flagged SensitiveEvidence answers with a patient
              // identifier. The CSV is plain text and gets shared, so the
              // evidence is withheld outright rather than truncated. The
              // marker is explicit so a blank cell is never mistaken for a
              // check that recorded nothing.
              c.sensitive
                ? "[withheld - patient identifier]"
                : (c.evidence||"").replace(/\s+/g," ").substring(0,500),
              c.reviewer_decision||"", c.reviewer_comment||""]);
            wrote++;
          }
        }
      }
    }
    // One row per case even with nothing to report, so a case never silently
    // vanishes from the export. The blank count tracks CHECK so the columns
    // stay aligned if it changes.
    if (wrote === 0) rows.push([...base, ...CHECK.map(() => "")]);
  }

  const csv = rows.map(r=>r.map(cell=>`"${String(cell??"").replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"});
  const a = document.createElement("a"); a.href=URL.createObjectURL(blob);
  a.download=`audit_${audience}_${new Date().toISOString().split("T")[0]}.csv`; a.click(); URL.revokeObjectURL(a.href);
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
export default function AuditPreview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ids = useMemo(() => (searchParams.get("ids") || "").split(",").filter(Boolean), [searchParams]);

  const [audience, setAudience] = useState("both");
  const [expandAll, setExpandAll] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ids.length === 0) { setLoading(false); return; }
    setLoading(true); setError(null);
    // The audience split is applied client-side, so switching it must not
    // refetch. The endpoint's audit_type is read but never used to filter, so
    // "combined" keeps its vocabulary unchanged.
    getFileAuditData(ids, "combined").then(setData).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [ids.join(",")]);

  if (ids.length === 0) return (
    <div className="page-body" style={{ padding:48, textAlign:"center" }}>
      <div style={{ fontSize:16, fontWeight:600, color:"var(--text-secondary)", marginBottom:8 }}>No files selected</div>
      <div style={{ fontSize:13, color:"var(--text-tertiary)", marginBottom:16 }}>Select files from the Documents page to generate an audit.</div>
      <button className="btn btn-primary btn-sm" onClick={() => navigate("/documents")}>← Go to Documents</button>
    </div>
  );

  const documents = data?.documents || [];
  const totalDocs = documents.length;
  const passedDocs = documents.filter(d => d.fail_count === 0).length;
  const alertDocs = documents.filter(d => d.alert_status === "ALERT" || d.alert_status === "MANUAL_ALERT").length;
  const avgScore = pooledScore(documents);

  return (
    <>
      <style>{`
        @media print {
          @page { margin: 12mm; }

          /* The app is a viewport-locked flex shell: .app-shell is
             height:100vh/overflow:hidden, .main-content is overflow:hidden and
             .page-body is its own scroll container. Printing a scroll
             container emits only what is on screen, which is why the PDF used
             to stop partway. Every one of them has to be unwound. */
          html, body { height:auto !important; overflow:visible !important; }
          .app-shell {
            display:block !important; height:auto !important;
            max-height:none !important; overflow:visible !important;
          }
          .main-content {
            display:block !important; height:auto !important;
            max-height:none !important; overflow:visible !important;
            margin:0 !important; padding:0 !important;
          }
          .page-body {
            flex:none !important; height:auto !important;
            max-height:none !important; overflow:visible !important;
            padding:0 !important;
          }

          .no-print { display:none !important; }
          .app-shell > :first-child { display:none !important; }

          /* Keep the header, but let it size to its content rather than the
             app's fixed --header-h, which cropped the title. */
          .page-header {
            position:static !important; display:flex !important;
            height:auto !important; min-height:0 !important;
            border:none !important; box-shadow:none !important;
            padding:0 0 10px 0 !important;
          }

          /* A card now holds an entire case - far taller than a page. The old
             break-inside:avoid told the browser not to split it, so it
             overflowed and clipped the checks instead of paginating. */
          .card {
            break-inside:auto !important; page-break-inside:auto !important;
            overflow:visible !important; box-shadow:none !important;
            border:1px solid #ddd !important; margin-bottom:8px !important;
          }

          /* Protect only the small units: the one-line check summary never
             splits, while its evidence block - which can run long once
             Expand all is on - paginates freely. Putting this on the whole
             row would reintroduce the clipping fixed on .card above. */
          .print-row { break-inside:avoid; page-break-inside:avoid; }
          .print-keep-next { break-after:avoid; page-break-after:avoid; }

          /* Pass/fail tints and heading colours carry meaning, so stop the
             browser dropping backgrounds. */
          * {
            -webkit-print-color-adjust:exact !important;
            print-color-adjust:exact !important;
          }
          body { font-size:10px !important; }
        }
      `}</style>

      <div className="page-header">
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button className="btn btn-ghost btn-sm no-print" onClick={() => navigate("/documents")}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{width:14,height:14}}><path d="M10 3L5 8l5 5" strokeLinecap="round" strokeLinejoin="round"/></svg> Documents
          </button>
          <div style={{ width:1, height:28, background:"var(--border)" }} className="no-print"/>
          <div className="page-title-group">
            <div className="page-title" style={{ fontSize:16 }}>File Audit</div>
            <div className="page-subtitle">{ids.length} file{ids.length > 1 ? "s" : ""} · {AUDIENCES.find(t => t.key === audience)?.label}</div>
          </div>
        </div>
        <div className="header-actions no-print" style={{ display:"flex", gap:6 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setExpandAll(v => !v)}
            disabled={loading || !data}
            title={expandAll
              ? "Collapse the evidence on every check"
              : "Open the evidence on every check. Whatever is open is what the PDF contains."}
            style={{ display:"flex", alignItems:"center", gap:5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {expandAll
                ? <><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/></>
                : <><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/></>}
            </svg>
            {expandAll ? "Collapse all" : "Expand all"}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => window.print()} disabled={loading || !data}
            title={expandAll
              ? "Evidence is expanded, so the PDF will include it"
              : "Evidence is collapsed. Use Expand all first to include it in the PDF."}
            style={{ display:"flex", alignItems:"center", gap:5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Export PDF
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => exportToCSV(documents, audience)} disabled={loading || documents.length === 0}
            style={{ display:"flex", alignItems:"center", gap:5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export CSV
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Audience selector */}
        <div className="no-print" style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap" }}>
          {AUDIENCES.map(t => {
            const active = audience === t.key;
            const c = AUDIENCE_COLORS[t.key] || AUDIENCE_COLORS.both;
            return (
              <button key={t.key} onClick={() => setAudience(t.key)} style={{
                padding:"8px 14px", borderRadius:8, cursor:"pointer",
                border:`1.5px solid ${active ? c.color : "var(--border)"}`,
                background:active ? c.bg : "var(--white)",
                color:active ? c.color : "var(--text-secondary)",
                fontWeight:active ? 600 : 400, fontFamily:"var(--font-mono)", fontSize:11,
                display:"flex", alignItems:"center", gap:6, transition:"all 0.15s"
              }}>
                <div style={{ textAlign:"left" }}><div>{t.label}</div><div style={{ fontSize:9, opacity:0.6, fontWeight:400 }}>{t.desc}</div></div>
              </button>
            );
          })}
        </div>

        {/* Stats — only for multi-file */}
        {!loading && data && documents.length > 1 && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10, marginBottom:16 }}>
            <StatCard label="Cases" value={totalDocs} color="var(--blue-600)"/>
            <StatCard label="Avg Score" value={avgScore == null ? "\u2014" : `${avgScore}%`} color={avgScore >= 80 ? "var(--success)" : avgScore >= 60 ? "#d97706" : "var(--error)"} sub={`${passedDocs} passed · ${totalDocs - passedDocs} failed`}/>
            <StatCard label="Passed" value={`${passedDocs}/${totalDocs}`} color="var(--success)"/>
            <StatCard label="With Alerts" value={alertDocs} color={alertDocs > 0 ? "var(--error)" : "var(--text-tertiary)"}/>
          </div>
        )}

        {loading && <div className="card" style={{ padding:48, textAlign:"center" }}><div style={{ fontSize:14, color:"var(--text-secondary)" }}>Loading audit data for {ids.length} file{ids.length > 1 ? "s" : ""}...</div></div>}
        {error && <div className="card" style={{ padding:32, textAlign:"center" }}><div style={{ fontSize:14, color:"var(--error)", marginBottom:8 }}>Failed to load audit data</div><div style={{ fontSize:12, color:"var(--text-tertiary)", marginBottom:12 }}>{error}</div><button className="btn btn-secondary btn-sm" onClick={() => window.location.reload()}>Retry</button></div>}

        {!loading && !error && data && (
          <>
            <AggregateSection documents={documents} audience={audience}/>
            {documents.map(d => (
              <CombinedView key={d.file_name} doc={d} audience={audience}
                expandAll={expandAll}/>
            ))}
          </>
        )}
      </div>
    </>
  );
}